import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { citiesAPI, routesAPI } from '../services/api';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const Cities = () => {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editCity, setEditCity] = useState(null);
  const [form, setForm] = useState({ name: '', route_id: '' });
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [routeFilter, setRouteFilter] = useState('');

  const { data: citiesData, isLoading } = useQuery('cities', citiesAPI.getAll);
  const { data: routesData } = useQuery('routes', routesAPI.getAll);

  const createMutation = useMutation(citiesAPI.create, {
    onSuccess: () => { setShowModal(false); setForm({ name: '', route_id: '' }); queryClient.invalidateQueries('cities'); },
    onError: (err) => setError(err.response?.data?.error || 'Failed to create city'),
  });
  const updateMutation = useMutation(({ id, ...data }) => citiesAPI.update(id, data), {
    onSuccess: () => { setShowModal(false); setEditCity(null); setForm({ name: '', route_id: '' }); queryClient.invalidateQueries('cities'); },
    onError: (err) => setError(err.response?.data?.error || 'Failed to update city'),
  });
  const deleteMutation = useMutation(citiesAPI.delete, {
    onSuccess: () => queryClient.invalidateQueries('cities'),
    onError: (err) => setError(err.response?.data?.error || 'Failed to delete city'),
  });

  const openAdd = () => { setEditCity(null); setForm({ name: '', route_id: '' }); setShowModal(true); setError(''); };
  const openEdit = (city) => { setEditCity(city); setForm({ name: city.name, route_id: city.route_id }); setShowModal(true); setError(''); };
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.route_id) { setError('Name and route are required'); return; }
    if (editCity) updateMutation.mutate({ id: editCity.id, ...form });
    else createMutation.mutate(form);
  };
  const handleDelete = (id) => { if (window.confirm('Delete this city?')) deleteMutation.mutate(id); };

  if (isLoading) return <LoadingSpinner />;
  const cities = citiesData?.cities || [];
  const routes = routesData?.routes || [];

  // Filter cities by search and route
  const filteredCities = cities.filter(city => {
    const matchesSearch = city.name.toLowerCase().includes(search.toLowerCase());
    const matchesRoute = !routeFilter || String(city.route_id) === String(routeFilter);
    return matchesSearch && matchesRoute;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Cities</h1>
        <button className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg" onClick={openAdd}>
          <Plus className="h-4 w-4 mr-2" /> Add City
        </button>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
        <input
          type="text"
          className="border rounded px-3 py-2 w-full sm:w-64"
          placeholder="Search by city name..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className="border rounded px-3 py-2 w-full sm:w-64"
          value={routeFilter}
          onChange={e => setRouteFilter(e.target.value)}
        >
          <option value="">All Routes</option>
          {routes.map(route => <option key={route.id} value={route.id}>{route.name}</option>)}
        </select>
      </div>
      <table className="min-w-full bg-white border rounded-lg">
        <thead>
          <tr>
            <th className="px-4 py-2 border-b">Name</th>
            <th className="px-4 py-2 border-b">Route</th>
            <th className="px-4 py-2 border-b">Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredCities.map(city => (
            <tr key={city.id}>
              <td className="px-4 py-2 border-b">{city.name}</td>
              <td className="px-4 py-2 border-b">{city.route_name}</td>
              <td className="px-4 py-2 border-b space-x-2">
                <button className="text-blue-600" onClick={() => openEdit(city)}><Edit className="inline h-4 w-4" /></button>
                <button className="text-red-600" onClick={() => handleDelete(city.id)}><Trash2 className="inline h-4 w-4" /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">{editCity ? 'Edit City' : 'Add City'}</h2>
              <button onClick={() => setShowModal(false)}><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block mb-1 font-medium">Name</label>
                <input type="text" className="w-full border rounded px-3 py-2" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <label className="block mb-1 font-medium">Route</label>
                <select className="w-full border rounded px-3 py-2" value={form.route_id} onChange={e => setForm(f => ({ ...f, route_id: e.target.value }))}>
                  <option value="">Select route</option>
                  {routes.map(route => <option key={route.id} value={route.id}>{route.name}</option>)}
                </select>
              </div>
              {error && <div className="text-red-600 text-sm">{error}</div>}
              <div className="flex justify-end space-x-2">
                <button type="button" className="px-4 py-2 bg-gray-200 rounded" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded flex items-center"><Save className="h-4 w-4 mr-1" />Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cities; 